import React, { useMemo } from 'react';

import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Tag, Typography } from 'antd';

import { SessionInspect as SessionInspectData } from './api';

const pct = (value: number | null | undefined, digits = 2) =>
  value === null || value === undefined
    ? '—'
    : `${(value * 100).toFixed(digits)}%`;

const money = (value: number, symbol?: string) =>
  `${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}${
    symbol ? ` ${symbol}` : ''
  }`;

const millions = (value: number, symbol?: string) =>
  `${(value / 1_000_000).toLocaleString(undefined, {
    maximumFractionDigits: 2,
  })}M${symbol ? ` ${symbol}` : ''}`;

const day = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString() : '—';

const Tile: React.FC<{
  label: string;
  value: React.ReactNode;
  tone?: 'pos' | 'neg';
  sub?: React.ReactNode;
}> = ({ label, value, tone, sub }) => (
  <div className="cstudio__tile">
    <span className="cstudio__tile-label">{label}</span>
    <span
      className="cstudio__tile-value"
      style={
        tone
          ? { color: tone === 'pos' ? 'var(--s2-positive)' : 'var(--s2-negative)' }
          : undefined
      }
    >
      {value}
    </span>
    {sub != null && <span className="cstudio__tile-sub">{sub}</span>}
  </div>
);

/** Assessment-session drill-down: session summary, player stat tiles, the
 *  return-vs-benchmark evolution chart, and the player's report. (Solo IDG
 *  rooms inspect as the skill tree with a grading overlay instead.) */
const SessionInspect: React.FC<{ data: SessionInspectData }> = ({ data }) => {
  const { session, player, series, ai_report: aiReport } = data;
  const symbol = session.reporting_currency?.symbol;

  const chartSeries = useMemo(() => {
    const playerPoints = series.map(point => ({
      x: new Date(point.t).getTime(),
      y: +(point.player * 100).toFixed(4),
    }));
    const out: { name: string; data: { x: number; y: number }[] }[] = [
      { name: 'Player return', data: playerPoints },
    ];
    if (session.has_benchmark) {
      out.push({
        name: 'Benchmark',
        data: series.map(point => ({
          x: new Date(point.t).getTime(),
          y: +(((point.benchmark ?? 0) as number) * 100).toFixed(4),
        })),
      });
    }
    return out;
  }, [series, session.has_benchmark]);

  const chartOptions = useMemo(
    (): ApexOptions => ({
      chart: {
        type: 'line',
        animations: { enabled: false },
        toolbar: { show: false },
        zoom: { enabled: false },
        fontFamily: 'inherit',
      },
      // Player carries the accent; the benchmark is context (muted gray).
      colors: ['#0f5fd6', '#8a97a8'],
      stroke: { width: 2, curve: 'straight' },
      xaxis: { type: 'datetime', labels: { datetimeUTC: false } },
      yaxis: {
        labels: { formatter: (value: number) => `${value.toFixed(1)}%` },
      },
      legend: { show: session.has_benchmark, position: 'top' },
      grid: { borderColor: 'rgba(216, 225, 239, 0.7)' },
      tooltip: {
        shared: true,
        y: { formatter: (value: number) => `${value.toFixed(2)}%` },
      },
    }),
    [session.has_benchmark],
  );

  const summaryRow = (label: string, value: React.ReactNode) => (
    <div className="cstudio__summary-row">
      <span className="cstudio__tile-label">{label}</span>
      <span>{value}</span>
    </div>
  );

  return (
    <div className="cstudio__sess-cols">
      {/* Column 1 — session summary, stat cards, return graph (stays put) */}
      <div className="cstudio__sess-col">
        <div className="cstudio__inspect-summary">
          {summaryRow('Starting cash', money(session.starting_cash, symbol))}
          {summaryRow(
            'Reporting currency',
            session.reporting_currency
              ? `${session.reporting_currency.name} (${session.reporting_currency.symbol})`
              : '—',
          )}
          {summaryRow(
            'Historical period',
            `${day(session.start_date)} → ${day(session.end_date)}`,
          )}
          {summaryRow(
            'Played asset classes',
            player.traded_asset_classes.length ? (
              <span>
                {player.traded_asset_classes.map(label => (
                  <Tag key={label} style={{ marginInlineEnd: 4 }}>
                    {label}
                  </Tag>
                ))}
              </span>
            ) : (
              'none traded'
            ),
          )}
        </div>

        <div className="cstudio__inspect-tiles">
          <Tile
            label="Return"
            value={pct(player.last_portfolio_return)}
            tone={player.last_portfolio_return >= 0 ? 'pos' : 'neg'}
          />
          <Tile
            label="PnL"
            value={millions(player.derived_pnl, symbol)}
            tone={player.derived_pnl >= 0 ? 'pos' : 'neg'}
          />
          <Tile
            label="Volatility"
            value={pct(player.last_metrics?.portfolio_volatility)}
          />
          <Tile
            label="Sharpe"
            value={player.last_metrics ? player.last_metrics.sharpe_ratio.toFixed(2) : '—'}
          />
          <Tile label="Max drawdown" value={pct(player.last_metrics?.mdd)} />
          <Tile
            label="Deals"
            value={player.deals}
            sub={player.auto_traded ? 'auto-traded' : undefined}
          />
        </div>

        <div className="cstudio__panel-card-title" style={{ margin: '14px 0 4px' }}>
          Return evolution{session.has_benchmark ? ' vs benchmark' : ''}
        </div>
        {series.length ? (
          <Chart type="line" height={240} series={chartSeries} options={chartOptions} />
        ) : (
          <Typography.Text type="secondary">
            No return history was recorded for this session.
          </Typography.Text>
        )}
      </div>

      {/* Column 2 — the player's report: the heading and outlined container
          stay put; only the markdown INSIDE the container scrolls. */}
      <div className="cstudio__sess-col cstudio__sess-col--report">
        <div className="cstudio__panel-card-title" style={{ margin: '0 0 4px' }}>
          Report
        </div>
        {aiReport ? (
          // Same markdown pipeline as the Analysis Studio report panel.
          <div className="cstudio__inspect-ai">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {aiReport.response}
            </ReactMarkdown>
          </div>
        ) : (
          <Typography.Text type="secondary">
            No report has been generated for this player yet.
          </Typography.Text>
        )}
      </div>
    </div>
  );
};

export default SessionInspect;
